"use client";

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import {
  adminMeControllerFindMyProducts,
  adminOrganizationControllerCreate,
  authControllerSwitchOrganization,
} from "@/lib/api/generated/faqrunAPI";
import type { ProductWithPermissionDtoPermission } from "@/lib/api/generated/faqrunAPI.schemas";
import type { OrganizationProductGroup, MyProduct, OrgRole } from "@/types/product";

export const STORAGE_KEY = "faqrun_selected_product_id";

interface FlatProduct extends MyProduct {
  organizationId: string;
  organizationName: string;
  orgRole: OrgRole;
}

interface ProductContextValue {
  selectedProductId: string | null;
  setSelectedProductId: (id: string) => void;
  organizationGroups: OrganizationProductGroup[];
  flatProducts: FlatProduct[];
  isLoading: boolean;
  selectedProduct: FlatProduct | null;
  currentPermission: ProductWithPermissionDtoPermission | null;
  currentOrgRole: OrgRole | null;
  createOrganization: (name: string) => Promise<void>;
}

const ProductContext = createContext<ProductContextValue | null>(null);

export function ProductProvider({ children }: { children: ReactNode }) {
  const [selectedProductId, setSelectedProductIdState] = useState<string | null>(null);
  const [organizationGroups, setOrganizationGroups] = useState<OrganizationProductGroup[]>([]);
  const [flatProducts, setFlatProducts] = useState<FlatProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadProducts = useCallback(async (): Promise<FlatProduct[]> => {
    const res = await adminMeControllerFindMyProducts();
    const groups: OrganizationProductGroup[] = res.data.map((g) => ({
      organizationId: g.organizationId,
      organizationName: g.organizationName,
      orgRole: g.orgRole,
      products: g.products.map((p) => ({
        id: p.id,
        name: p.name,
        permission: p.permission,
        publicSiteUrl: p.publicSiteUrl ?? null,
      })),
    }));
    setOrganizationGroups(groups);

    const flat: FlatProduct[] = groups.flatMap((group) =>
      group.products.map((p) => ({
        ...p,
        organizationId: group.organizationId,
        organizationName: group.organizationName,
        orgRole: group.orgRole,
      })),
    );
    setFlatProducts(flat);
    return flat;
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const flat = await loadProducts();

        const storedId = localStorage.getItem(STORAGE_KEY);
        if (storedId && flat.some((p) => p.id === storedId)) {
          setSelectedProductIdState(storedId);
        } else {
          const firstProduct = flat[0];
          if (firstProduct) {
            setSelectedProductIdState(firstProduct.id);
            localStorage.setItem(STORAGE_KEY, firstProduct.id);
          }
        }
      } catch (error) {
        console.error("Failed to load products:", error);
      } finally {
        setIsLoading(false);
      }
    }

    init();
  }, [loadProducts]);

  const setSelectedProductId = useCallback(
    async (id: string) => {
      const target = flatProducts.find((p) => p.id === id);
      const current = flatProducts.find((p) => p.id === selectedProductId);

      // 組織が異なる場合は JWT を再発行（Set-Cookie で自動更新）
      if (target && current && target.organizationId !== current.organizationId) {
        try {
          await authControllerSwitchOrganization({
            organizationId: target.organizationId,
          });
          // トークンはサーバーが Set-Cookie で更新するため、明示的な保存は不要
        } catch (error) {
          console.error("Failed to switch organization:", error);
          return;
        }
      }

      setSelectedProductIdState(id);
      localStorage.setItem(STORAGE_KEY, id);

      // 組織切り替え後はデータを再取得するためリロード
      if (target && current && target.organizationId !== current.organizationId) {
        window.location.reload();
      }
    },
    [flatProducts, selectedProductId],
  );

  const createOrganization = useCallback(
    async (name: string) => {
      const created = await adminOrganizationControllerCreate({ name });
      if (created.status !== 201) {
        throw new Error("組織の作成に失敗しました");
      }
      const newOrganizationId = created.data.id;

      // 新しい組織へ JWT を切り替える（Set-Cookie で自動更新）
      const switched = await authControllerSwitchOrganization({
        organizationId: newOrganizationId,
      });
      if (switched.status !== 200) {
        throw new Error(
          "組織は作成されましたが、切り替えに失敗しました。ページを再読み込みしてから組織を選び直してください。",
        );
      }

      const flat = await loadProducts();
      const newProduct = flat.find((p) => p.organizationId === newOrganizationId);
      if (newProduct) {
        localStorage.setItem(STORAGE_KEY, newProduct.id);
      }

      // 組織切り替え後はデータを再取得するためリロード
      window.location.reload();
    },
    [loadProducts],
  );

  const selectedProduct = flatProducts.find((p) => p.id === selectedProductId) ?? null;
  const currentPermission = selectedProduct?.permission ?? null;
  const currentOrgRole = selectedProduct?.orgRole ?? null;

  return (
    <ProductContext.Provider
      value={{
        selectedProductId,
        setSelectedProductId,
        organizationGroups,
        flatProducts,
        isLoading,
        selectedProduct,
        currentPermission,
        currentOrgRole,
        createOrganization,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProduct must be used within a ProductProvider");
  }
  return context;
}
